import { useEffect, useLayoutEffect, useRef, useState } from "react";

const SectionCard = ({
	title,
	icon,
	children,
	loading = false,
	collapsible = true,
	defaultOpen = true,
	onToggle,

	backVisible = false,
	backContent = null,
	backMode = "flip",
	onBackToggle,
	backHeight = 320,
}) => {
	const [open, setOpen] = useState(defaultOpen);
	const contentRef = useRef(null);
	const [maxH, setMaxH] = useState("0px");
	const [ready, setReady] = useState(false);
	const [contentH, setContentH] = useState(0);

	const toggle = () => {
		const next = !open;
		setOpen(next);
		onToggle?.(next);
	};

	useLayoutEffect(() => {
		if (!contentRef.current) return;
		setMaxH(defaultOpen ? "none" : "0px");
		const h = contentRef.current.scrollHeight || 0;
		setContentH(defaultOpen ? h : 0);
		requestAnimationFrame(() => setReady(true));
	}, []);

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		const end = () => setMaxH("none");

		if (open) {
			const h = el.scrollHeight;

			if (maxH === "none") {
				setMaxH("0px");
				requestAnimationFrame(() => {
					setMaxH(h + "px");
				});
			} else {
				setMaxH(h + "px");
			}

			setMaxH(h + "px");

			const onEnd = (e) => {
				if (e.propertyName === "max-height") end();
			};
			el.addEventListener("transitionend", onEnd, { once: true });
			return () => el.removeEventListener("transitionend", onEnd);
		} else {
			const current = el.scrollHeight;
			setMaxH(current + "px");
			requestAnimationFrame(() => setMaxH("0px"));
		}
	}, [open]);


	useEffect(() => {
		if (!contentRef.current) return;
		const el = contentRef.current;
		const ro = new ResizeObserver(() => {
			if (open) {
				const h = el.scrollHeight;
				if (maxH !== "none") setMaxH(h + "px");
				setContentH(h);
			}
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [open, maxH]);

	// hearder
	const Header = (
		<div className={`flex items-center justify-between ${open ? "mb-4" : ""} ${ready ? "transition-all duration-500" : ""}`}>
			<div className="flex items-center gap-2">
				<div className="flex justify-center items-center bg-orange-600 rounded-lg w-9 h-9 text-white">
					<i className={`fa-solid ${icon}`} />
				</div>
				<h5 className="font-semibold text-gray-800">{title}</h5>
			</div>
			<div className="flex items-center gap-2">
				{backContent && (
					<button
						type="button"
						onClick={() => onBackToggle?.(!backVisible)}
						aria-pressed={!!backVisible}
						title={backVisible ? "Masquer l’arrière" : "Voir l’arrière (carte)"}
						className="flex justify-center items-center hover:bg-orange-50 rounded-lg w-9 h-9 text-orange-600 transition"
					>
						<i className={`fa-solid fa-layer-group ${backVisible ? "opacity-100" : "opacity-70"}`} />
					</button>
				)}
				{collapsible && (
					<button
						type="button"
						onClick={toggle}
						aria-expanded={open}
						className={`flex h-9 w-9 items-center justify-center text-white ${ready ? "transition-transform" : ""}`}
						title={open ? "Réduire" : "Déployer"}
					>
						<i
							className={`fa-solid fa-chevron-right transform ${ready ? "transition-transform duration-200" : ""} text-orange-600 ${open ? "rotate-90" : "rotate-0"}`}
						/>
					</button>
				)}
			</div>
		</div>
	);

	// Corps avant (front body) — inchangé côté calcul
	const FrontBody = (
		<div
			ref={contentRef}
			className={`overflow-hidden ${ready ? "transition-[max-height,opacity] duration-300 ease-out" : ""} ${open ? "opacity-100" : "opacity-0"}`}
			style={{ maxHeight: maxH }}
		>
			{loading ? (
				<div className="flex flex-col flex-1 gap-5 sm:p-2">
					<div className="flex flex-col flex-1 gap-3">
						<div className="bg-gray-200 rounded-2xl w-full h-3 animate-pulse" />
						<div className="bg-gray-200 rounded-2xl w-full h-3 animate-pulse" />
						<div className="bg-gray-200 rounded-2xl w-full h-3 animate-pulse" />
						<div className="bg-gray-200 rounded-2xl w-full h-3 animate-pulse" />
					</div>
					<div className="flex gap-3 mt-auto">
						<div className="bg-gray-200 rounded-full w-20 h-8 animate-pulse" />
						<div className="bg-gray-200 rounded-full w-20 h-8 animate-pulse" />
						<div className="bg-gray-200 ml-auto rounded-full w-20 h-8 animate-pulse" />
					</div>
				</div>
			) : (
				children
			)}
		</div>
	);

	// Face arrière (back body) — prend EXACTEMENT la même hauteur que le body avant
	const BackBody = (
		<div
			className={`overflow-hidden ${ready ? "transition-[height,opacity] duration-300 ease-out" : ""} ${open ? "opacity-100" : "opacity-0"}`}
			style={{ height: open ? contentH : 0 }}
			aria-hidden={!backVisible}
		>
			{/* le contenu arrière remplit 100% de la hauteur calculée */}
			<div className="relative rounded-lg w-full h-full overflow-hidden">
				{backContent}
			</div>
		</div>
	);

	// Mode SLIDE (inchangé)
	const SlideBack = backContent && backMode === "slide" && (
		<div
			className={[
				"relative overflow-hidden rounded-lg",
				backVisible ? `mt-3 h-[${backHeight}px]` : "h-0",
				"transition-[height,margin] duration-300 ease-out",
			].join(" ")}
			style={{ height: backVisible ? backHeight : 0, marginTop: backVisible ? 12 : 0 }}
			aria-hidden={!backVisible}
		>
			<div className="absolute inset-0 rounded-lg w-full h-full overflow-hidden">
				{backContent}
			</div>
		</div>
	);

	// 🔁 MODE FLIP : toute la carte (header+body) pivote
	if (backContent && backMode === "flip") {
		return (
			<div className="perspective-[1200px]">
				<div
					className={[
						"relative transition-transform duration-500 ease-out preserve-3d",
						backVisible ? "rotate-y-180" : "rotate-y-0",
					].join(" ")}
					style={{ transformStyle: "preserve-3d" }}
				>
					{/* FACE AVANT : carte complète */}
					<div
						className="backface-hidden"
						style={{ backfaceVisibility: "hidden" }}
					>
						<div className="bg-white shadow-lg p-4 md:p-6 rounded-lg">
							{Header}
							{FrontBody}
						</div>
					</div>

					{/* FACE ARRIÈRE : carte complète */}
					<div
						className="absolute inset-0 rotate-y-180 backface-hidden"
						style={{ backfaceVisibility: "hidden" }}
					>
						<div className="bg-white shadow-lg p-4 md:p-6 rounded-lg">
							{Header}
							{BackBody}
						</div>
					</div>
				</div>

				{/* utilitaires flip */}
				<style>{`
          .preserve-3d { transform-style: preserve-3d; }
          .backface-hidden { backface-visibility: hidden; }
          .rotate-y-180 { transform: rotateY(180deg); }
          .rotate-y-0   { transform: rotateY(0deg); }
          .perspective-[1200px] { perspective: 1200px; }
        `}</style>
			</div>
		);
	}

	// Par défaut (pas de flip) : rendu classique + mode slide optionnel
	return (
		<div className="bg-white shadow-lg p-4 md:p-6 rounded-lg">
			{Header}
			{FrontBody}
			{SlideBack}
		</div>
	);
};

export default SectionCard;
